/**
 * Dialogen zoals het origineel ze toonde.
 *
 * Een gecentreerd parkementkader met links de zonsondergang-miniatuur, rechts de vraag,
 * en eronder de originele knoppen — die dragen hun Nederlandse opschrift al in het
 * plaatje. Zo zag "Wil je een kaart ruilen?" er in de cd-versie uit.
 *
 * Gebeurtenissen krijgen dezelfde behandeling, maar dan met de banner uit
 * `30-interface/gebeurtenis-banners/` in plaats van de zonsondergang.
 */
import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import { asset, hasAsset, klik } from './assets.js';
import {
  arcering,
  knop,
  negenStuk,
  RASTER_BREED,
  RASTER_HOOG,
  tekst,
  type KnopOpties,
} from './skin.js';

const BREED = 400;
const HOOG = 118;
const TOP = 100;

export interface DialoogKnop {
  readonly soort: KnopOpties['soort'];
  readonly label?: string;
  readonly onClick: () => void;
}

export interface DialoogOpties {
  readonly vraag: string;
  readonly knoppen: readonly DialoogKnop[];
  /** Plaatje links; standaard de zonsondergang uit het origineel. */
  readonly beeld?: { groep: string; naam: string };
}

/** Bouwt een dialoog. De aanroeper hangt hem in de HUD-laag en haalt hem weer weg. */
export function dialoog(opties: DialoogOpties): Container {
  const doos = new Container();
  doos.x = (RASTER_BREED - BREED) / 2;
  doos.y = TOP;

  doos.addChild(negenStuk('MENU_092', BREED, HOOG, 14));

  // Het plaatje links: de zonsondergang, of de banner van een gebeurtenis.
  const bron = opties.beeld ?? { groep: 'symbolen-groot', naam: 'ASYMBOL_003' };
  if (hasAsset(bron.groep, bron.naam)) {
    const beeld = new Sprite(Texture.from(asset(bron.groep, bron.naam).src));
    beeld.setSize(92, 92);
    beeld.x = 10;
    beeld.y = 13;
    doos.addChild(beeld);
  }

  const vraag = tekst(opties.vraag, 15);
  vraag.style.wordWrap = true;
  vraag.style.wordWrapWidth = BREED - 130;
  vraag.x = 114;
  vraag.y = 14;
  doos.addChild(vraag);

  // De knoppen op een rij onderaan, gecentreerd in de ruimte rechts van het plaatje.
  const knopBreed = 109;
  const kier = 12;
  const totaal = opties.knoppen.length * (knopBreed + kier) - kier;
  const start = 114 + (BREED - 124 - totaal) / 2;
  for (const [i, k] of opties.knoppen.entries()) {
    const el = knop({
      soort: k.soort,
      ...(k.label === undefined ? {} : { label: k.label }),
      onClick: k.onClick,
    });
    el.x = start + i * (knopBreed + kier);
    el.y = HOOG - 52;
    doos.addChild(el);
  }
  return doos;
}

/**
 * De worpkeuze van de Alchimist: zes dobbelsteenvlakken om uit te kiezen.
 *
 * Een gewone dialoog krijgt zes knoppen niet kwijt (het kader is 400 breed, een knop
 * 109), en de keuze ís geen tekst maar een worp — dus staan de vlakken er zelf,
 * klikbaar, met de platen die de worp-animatie ook gebruikt.
 */
export function worpKeuze(opties: {
  readonly vraag: string;
  readonly waarden: readonly number[];
  readonly onKies: (waarde: number) => void;
}): Container {
  const doos = new Container();
  doos.x = (RASTER_BREED - BREED) / 2;
  doos.y = TOP;
  doos.addChild(negenStuk('MENU_092', BREED, HOOG, 14));

  const vraag = tekst(opties.vraag, 15);
  vraag.style.wordWrap = true;
  vraag.style.wordWrapWidth = BREED - 28;
  vraag.x = 14;
  vraag.y = 12;
  doos.addChild(vraag);

  const maat = 44;
  const kier = 12;
  const totaal = opties.waarden.length * (maat + kier) - kier;
  const start = (BREED - totaal) / 2;
  for (const [i, waarde] of opties.waarden.entries()) {
    if (!hasAsset('dobbelstenen', `opbrengst-${waarde}`)) continue;
    const vlak = new Sprite(Texture.from(asset('dobbelstenen', `opbrengst-${waarde}`).src));
    vlak.setSize(maat, maat);
    vlak.x = start + i * (maat + kier);
    vlak.y = HOOG - maat - 14;
    vlak.eventMode = 'static';
    vlak.cursor = 'pointer';
    vlak.on('pointertap', () => {
      klik();
      opties.onKies(waarde);
    });
    vlak.on('pointerover', () => (vlak.y = HOOG - maat - 20));
    vlak.on('pointerout', () => (vlak.y = HOOG - maat - 14));
    doos.addChild(vlak);
  }
  return doos;
}

/**
 * De banner die bij een gebeurtenis hoort.
 *
 * De vijf banners staan in dezelfde volgorde als de vlakken van de dobbelsteen:
 * feestmaal, handelaar, overval, toernooi, brandende stad.
 */
const BANNER_VAN: Readonly<Record<string, string>> = {
  'goed-jaar': 'EVENT_000',
  handelsvoordeel: 'EVENT_001',
  roofoverval: 'EVENT_002',
  riddertoernooi: 'EVENT_003',
  gebeurteniskaart: 'EVENT_004',
  // De twee speelfiguren lenen de plaat die bij hen hoort: de stekende ridders voor de
  // riddermacht, de handelaar voor de molen. Eigen platen zijn er niet, en deze twee
  // dekken de lading precies.
  riddermacht: 'EVENT_003',
  handelsmacht: 'EVENT_001',
};

/**
 * Een gebeurtenis in beeld: de banner met een korte tekst eronder.
 *
 * De vijf platen `EVENT_000`–`EVENT_004` horen bij de vijf vlakken van de
 * gebeurtenissendobbelsteen. Alleen het vraagteken heeft er geen eigen plaat voor: dat
 * vlak trekt een kaart, en dán hoort de illustratie van *die* kaart in de banner te
 * staan. Anders krijgt de Pest hetzelfde vuurbeeld als de Vooruitgang.
 */
export function gebeurtenisBanner(
  gebeurtenis: string,
  uitleg: string,
  kaartId?: number,
): Container | undefined {
  const naam = BANNER_VAN[gebeurtenis];
  const eigenKaart = kaartId !== undefined && hasAsset('kaarten', String(kaartId));
  if (!eigenKaart && (naam === undefined || !hasAsset('banners', naam))) return undefined;

  const doos = new Container();
  const breed = 320;
  // Ruim genoeg voor twee regels uitleg onder de banner.
  const hoog = 176;
  doos.x = (RASTER_BREED - breed) / 2;
  doos.y = 120;
  doos.addChild(negenStuk('MENU_092', breed, hoog, 14));

  if (eigenKaart) {
    // De kaartplaat is vierkant; we tonen er een strook van uit, uit het bovenste deel
    // waar de illustratie zit (het tekstbalkje begint pas op 65% hoogte).
    const venster = new Container();
    venster.x = (breed - 178) / 2;
    venster.y = 12;
    const plaat = new Sprite(Texture.from(asset('kaarten', String(kaartId)).src));
    plaat.setSize(178, 178);
    plaat.y = -18;
    venster.addChild(plaat);
    const masker = new Graphics().rect(0, 0, 178, 100).fill({ color: 0xffffff });
    venster.addChild(masker);
    venster.mask = masker;
    doos.addChild(venster);
  } else {
    const beeld = new Sprite(Texture.from(asset('banners', naam!).src));
    beeld.setSize(178, 100);
    beeld.x = (breed - 178) / 2;
    beeld.y = 12;
    doos.addChild(beeld);
  }

  const label = tekst(uitleg, 14);
  label.anchor.set(0.5, 0);
  label.style.wordWrap = true;
  label.style.wordWrapWidth = breed - 24;
  label.x = breed / 2;
  label.y = 120;
  doos.addChild(label);
  return doos;
}

// ------------------------------------------------------------------ ruilen

/** Wat het ruilpaneel per grondstof moet weten. */
export interface RuilRegel {
  readonly soort: string;
  /** Hoeveel je van deze soort moet inleveren voor één nieuwe grondstof. */
  readonly koers: number;
  /** Hoeveel je er nu hebt. */
  readonly voorraad: number;
}

export interface RuilOpties {
  readonly regels: readonly RuilRegel[];
  readonly onKies: (soort: string) => void;
  readonly onAnnuleer: () => void;
}

/**
 * Ruilen met de bank, op het ruilpaneel van het origineel.
 *
 * Per grondstof zie je het symbool, de koers en wat je in kas hebt. Heb je te weinig,
 * dan gaat de diagonale arcering — het "kan nu niet"-plaatje uit de skin — over de regel
 * heen, net als bij de bouwstapels. Zo zie je in één oogopslag wat kan.
 */
/**
 * Een losse mededeling op parkement, onderin het bord.
 *
 * Het origineel meldt zo bijvoorbeeld "Het handelsaanbod is afgewezen!" — een breed vak
 * met één regel erin, opgemeten op `catan-ref/shots/mij/handelsaanbod-afgewezen.png`
 * (x 313, y 571, 397×120). Geen knop: hij verdwijnt vanzelf.
 */
export function mededeling(regel: string): Container {
  const breed = 397;
  const hoog = 120;
  const doos = new Container();
  doos.x = 313;
  doos.y = 571;
  doos.addChild(negenStuk('MENU_092', breed, hoog, 14));

  const label = tekst(regel, 15);
  label.anchor.set(0.5, 0.5);
  label.style.wordWrap = true;
  label.style.wordWrapWidth = breed - 40;
  label.x = breed / 2;
  label.y = hoog / 2;
  doos.addChild(label);
  return doos;
}

export function ruilPaneel(opties: RuilOpties): Container {
  const regelHoog = 34;
  const breed = 340;
  const kop = 34;
  const voet = 54;
  const hoog = kop + opties.regels.length * regelHoog + voet;

  const doos = new Container();
  doos.x = (RASTER_BREED - breed) / 2;
  doos.y = 150;
  doos.addChild(negenStuk('MENU_109', breed, hoog, 14));

  const titel = tekst('Ruilen met de bank', 16);
  titel.anchor.set(0.5, 0);
  titel.x = breed / 2;
  titel.y = 10;
  doos.addChild(titel);

  for (const [i, regel] of opties.regels.entries()) {
    const rij = new Container();
    rij.x = 16;
    rij.y = kop + i * regelHoog;

    if (hasAsset('symbolen', regel.soort)) {
      const icoon = new Sprite(Texture.from(asset('symbolen', regel.soort).src));
      icoon.setSize(24, 24);
      rij.addChild(icoon);
    }

    const label = tekst(`${regel.koers}× ${regel.soort}  (je hebt ${regel.voorraad})`, 14);
    label.x = 32;
    label.y = 4;
    rij.addChild(label);

    const kan = regel.voorraad >= regel.koers;
    if (kan) {
      rij.eventMode = 'static';
      rij.cursor = 'pointer';
      rij.on('pointertap', () => {
        klik();
        opties.onKies(regel.soort);
      });
      rij.on('pointerover', () => (rij.alpha = 0.75));
      rij.on('pointerout', () => (rij.alpha = 1));
    } else {
      rij.addChild(arcering(breed - 32, regelHoog - 4));
    }
    doos.addChild(rij);
  }

  const terug = knop({ soort: 'annuleren', onClick: opties.onAnnuleer });
  terug.x = (breed - 109) / 2;
  terug.y = hoog - 48;
  doos.addChild(terug);
  return doos;
}

// ------------------------------------------------------------ plaatsen

/**
 * "Waar moet gebouwd worden? Klik op een oplichtend bouwveld."
 *
 * Het paneel dat het origineel toont zodra je een kaart of bouwwerk hebt gekozen: links
 * een miniatuur van wat je neerzet, rechts de aanwijzing, en een `Annuleren`-knop
 * (`catan-ref/shots/mij/kerk-bouwen.png`).
 */
export function plaatsingsPaneel(opties: {
  readonly cardId?: number;
  readonly beeld?: { groep: string; naam: string };
  readonly tekst: string;
  /** Wat de knop doet heet niet altijd "annuleren"; bij de opzet is het "Klaar". */
  readonly knopSoort?: KnopOpties['soort'];
  readonly knopLabel?: string;
  readonly onAnnuleer: () => void;
  /** Een tweede knop ernaast — de opzet gebruikt hem voor "Verdeel voor mij". */
  readonly tweedeSoort?: KnopOpties['soort'];
  readonly tweedeLabel?: string;
  readonly onTweede?: () => void;
}): Container {
  const breed = 400;
  const hoog = 108;
  const doos = new Container();
  doos.x = (RASTER_BREED - breed) / 2;
  doos.y = 100;
  doos.addChild(negenStuk('MENU_092', breed, hoog, 14));

  // De miniatuur links: de kaart die je plaatst, of het bouwwerk.
  const bron =
    opties.cardId !== undefined && hasAsset('kaarten', String(opties.cardId))
      ? { groep: 'kaarten', naam: String(opties.cardId) }
      : opties.beeld;
  if (bron !== undefined && hasAsset(bron.groep, bron.naam)) {
    const beeld = new Sprite(Texture.from(asset(bron.groep, bron.naam).src));
    beeld.setSize(84, 84);
    beeld.x = 10;
    beeld.y = 12;
    doos.addChild(beeld);
  }

  const label = tekst(opties.tekst, 15);
  label.style.wordWrap = true;
  label.style.wordWrapWidth = breed - 130;
  label.x = 106;
  label.y = 14;
  doos.addChild(label);

  const terug = knop({
    soort: opties.knopSoort ?? 'annulerenSmal',
    ...(opties.knopLabel === undefined ? {} : { label: opties.knopLabel }),
    onClick: opties.onAnnuleer,
  });
  terug.x = 106;
  terug.y = hoog - 46;
  doos.addChild(terug);

  if (opties.onTweede !== undefined) {
    const tweede = knop({
      soort: opties.tweedeSoort ?? 'ok',
      ...(opties.tweedeLabel === undefined ? {} : { label: opties.tweedeLabel }),
      onClick: opties.onTweede,
    });
    tweede.x = 226;
    tweede.y = hoog - 46;
    doos.addChild(tweede);
  }
  return doos;
}

// ------------------------------------------------------------ einde

/**
 * Het eindscherm: een schermvullende plaat met de uitslag erboven.
 *
 * Het origineel toont een van de acht tekeningen uit `50-eindschermen` op het hele
 * scherm, met een parkementstrook bovenaan waarin de score staat — "Je hebt 965 punten
 * behaald..." (`catan-ref/shots/mij/eindscherm-2.png`). Geen knoppenbalk eromheen: je
 * klikt om verder te gaan.
 */
/**
 * Welke van de acht eindtekeningen hoort bij deze uitslag?
 *
 * De acht lopen van vernietigende nederlaag naar verpletterende overwinning, en de acht
 * stukken in `END.BCF` lopen mee: **tekening N hoort bij `END_(N-1)`**. Dat is gemeten —
 * na een gewonnen potje klonk `END_007` terwijl tekening 8 in beeld stond, vastgesteld
 * door de schermopname naast alle acht te leggen (verschil 0,08 tegen 0,20 voor de
 * eerstvolgende).
 *
 * Het verschil in overwinningspunten bepaalt hoe overtuigend het was: nipt of ruim.
 */
export function eindTekening(gewonnen: boolean, puntenVerschil: number): number {
  const ruim = Math.min(3, Math.abs(puntenVerschil));
  // Verliezen: 1 is vernietigend, 4 is nipt. Winnen: 5 is met pijn en moeite, 8 is
  // verpletterend.
  return gewonnen ? 5 + ruim : 4 - ruim;
}

export function eindScherm(opties: {
  readonly gewonnen: boolean;
  readonly tekst: string;
  readonly puntenVerschil?: number;
  readonly onVerder: () => void;
}): Container {
  const doos = new Container();

  const naam = String(eindTekening(opties.gewonnen, opties.puntenVerschil ?? 0));
  if (hasAsset('eindschermen', naam)) {
    const plaat = new Sprite(Texture.from(asset('eindschermen', naam).src));
    plaat.setSize(RASTER_BREED, RASTER_HOOG);
    doos.addChild(plaat);
  }

  const breed = 400;
  const hoog = 106;
  const strook = negenStuk('MENU_092', breed, hoog, 14);
  strook.x = (RASTER_BREED - breed) / 2;
  strook.y = 60;
  doos.addChild(strook);

  const label = tekst(opties.tekst, 16);
  label.style.wordWrap = true;
  label.style.wordWrapWidth = breed - 32;
  label.x = (RASTER_BREED - breed) / 2 + 16;
  label.y = 74;
  doos.addChild(label);

  // Het hele scherm is de knop.
  doos.eventMode = 'static';
  doos.cursor = 'pointer';
  doos.on('pointertap', () => {
    klik();
    opties.onVerder();
  });
  return doos;
}

// ------------------------------------------------------------ betalen

/**
 * Het betaalpaneel: wat je bouwt, en wat je er nog voor moet neerleggen.
 *
 * De kosten tellen af terwijl je grondstoffen aanklikt. Zonder die aftelling klik je in
 * het duister en merk je pas achteraf of je genoeg had.
 */
export function betaalPaneel(opties: {
  readonly titel: string;
  readonly cardId?: number;
  readonly beeld?: { groep: string; naam: string };
  readonly resterend: ReadonlyMap<string, number>;
  readonly onAnnuleer: () => void;
}): Container {
  const breed = 400;
  const hoog = 118;
  const doos = new Container();
  doos.x = (RASTER_BREED - breed) / 2;
  doos.y = 100;
  doos.addChild(negenStuk('MENU_092', breed, hoog, 14));

  const bron =
    opties.cardId !== undefined && hasAsset('kaarten', String(opties.cardId))
      ? { groep: 'kaarten', naam: String(opties.cardId) }
      : opties.beeld;
  if (bron !== undefined && hasAsset(bron.groep, bron.naam)) {
    const beeld = new Sprite(Texture.from(asset(bron.groep, bron.naam).src));
    beeld.setSize(84, 84);
    beeld.x = 10;
    beeld.y = 12;
    doos.addChild(beeld);
  }

  const kop = tekst(opties.titel, 15);
  kop.x = 106;
  kop.y = 12;
  doos.addChild(kop);

  // De grondstoffen die nog moeten komen, als iconen met hun aantal.
  let x = 106;
  let nogIets = false;
  for (const [soort, aantal] of opties.resterend) {
    if (aantal <= 0) continue;
    nogIets = true;
    if (hasAsset('symbolen', soort)) {
      const icoon = new Sprite(Texture.from(asset('symbolen', soort).src));
      icoon.setSize(22, 22);
      icoon.x = x;
      icoon.y = 36;
      doos.addChild(icoon);
    }
    const label = tekst(`×${aantal}`, 14);
    label.x = x + 24;
    label.y = 40;
    doos.addChild(label);
    x += 56;
  }
  if (!nogIets) {
    const klaar = tekst('Betaald — klik nu de plek aan.', 14, 0x8fdc6a);
    klaar.x = 106;
    klaar.y = 38;
    doos.addChild(klaar);
  }

  const terug = knop({ soort: 'annulerenSmal', onClick: opties.onAnnuleer });
  terug.x = 106;
  terug.y = hoog - 46;
  doos.addChild(terug);
  return doos;
}
